//! Backoffice: configurazione provider di pagamento (NOWPayments).
//! Admin-only (la guard è già nel router). Le secret non vengono mai mostrate
//! in chiaro: il backend restituisce solo i valori masked, usati come
//! placeholder negli input. Lasciare un campo vuoto al salvataggio significa
//! "non sovrascrivere" — così non si perde una secret già configurata.

import { useEffect, useState } from "react";

import { authFetch } from "@/api/client";
import { Pill } from "@/components/Pill";
import { useStore } from "@/state/store";

interface PaymentConfig {
  provider: string;
  api_key_masked: string | null;
  ipn_secret_set: boolean;
  public_key_masked: string | null;
  sandbox: boolean;
  enabled: boolean;
  updated_at: string | null;
}

interface TestResult {
  ok: boolean;
  message: string;
}

/** Estrae un messaggio leggibile da un body di errore (ProblemDetails / JSON
 *  generico), con fallback sullo status code. */
async function extractError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string; message?: string };
    return body.detail ?? body.message ?? `Errore (${res.status})`;
  } catch {
    return `Errore (${res.status})`;
  }
}

export function PaymentConfigsPage() {
  const showToast = useStore((s) => s.showToast);

  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Form state. Le secret restano vuote: si compilano solo per aggiornarle.
  const [apiKey, setApiKey] = useState("");
  const [ipnSecret, setIpnSecret] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [sandbox, setSandbox] = useState(true);
  const [enabled, setEnabled] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await authFetch("/v1/admin/payment-configs/nowpayments");
      if (!res.ok) throw new Error(await extractError(res));
      const data = (await res.json()) as PaymentConfig;
      setConfig(data);
      setSandbox(data.sandbox);
      setEnabled(data.enabled);
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error
            ? `Errore nel caricamento config: ${e.message}`
            : "Errore nel caricamento config",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      // Ometti le secret/key vuote così il backend non le sovrascrive.
      const body: Record<string, unknown> = { sandbox, enabled };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      if (ipnSecret.trim()) body.ipn_secret = ipnSecret.trim();
      if (publicKey.trim()) body.public_key = publicKey.trim();

      const res = await authFetch("/v1/admin/payment-configs/nowpayments", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await extractError(res));

      showToast({ kind: "success", message: "Configurazione salvata" });
      setApiKey("");
      setIpnSecret("");
      setPublicKey("");
      await load();
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error
            ? `Errore salvataggio: ${e.message}`
            : "Errore salvataggio",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    try {
      const res = await authFetch(
        "/v1/admin/payment-configs/nowpayments/test",
        { method: "POST" },
      );
      const data = (await res.json()) as TestResult;
      showToast({
        kind: data.ok ? "success" : "error",
        message: data.ok ? data.message || "Connessione OK" : data.message,
      });
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error ? `Test fallito: ${e.message}` : "Test fallito",
      });
    } finally {
      setTesting(false);
    }
  }

  const updatedAtLabel = config?.updated_at
    ? new Date(config.updated_at).toLocaleString("it-IT")
    : null;

  return (
    <div className="flex flex-col gap-6 max-w-[760px]">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold">Payment Configs</h1>
          <p className="text-[13px] text-muted mt-1">
            Configurazione del provider di pagamento per i depositi crypto.
            Pagina riservata agli admin.
          </p>
        </div>
      </header>

      <InfoBox />

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : (
        <section className="card flex flex-col gap-5">
          {/* Provider header */}
          <div className="flex items-center justify-between gap-3 border-b border-line pb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-[16px] font-semibold">NOWPayments</h2>
                <Pill tone={enabled ? "yes" : "no"}>
                  {enabled ? "Attivo" : "Disattivo"}
                </Pill>
                <Pill tone={sandbox ? "warn" : "accent"}>
                  {sandbox ? "Sandbox" : "Produzione"}
                </Pill>
              </div>
              <p className="text-[12px] text-muted mt-1">
                Provider per depositi in criptovaluta.
              </p>
            </div>
          </div>

          {/* Credenziali */}
          <div className="flex flex-col gap-4">
            <SecretField
              label="API Key"
              value={apiKey}
              onChange={setApiKey}
              placeholder={
                config?.api_key_masked
                  ? config.api_key_masked
                  : "Inserisci l'API key"
              }
              configured={Boolean(config?.api_key_masked)}
              disabled={saving}
            />

            <SecretField
              label="Public Key"
              value={publicKey}
              onChange={setPublicKey}
              placeholder={
                config?.public_key_masked
                  ? config.public_key_masked
                  : "Inserisci la public key"
              }
              configured={Boolean(config?.public_key_masked)}
              disabled={saving}
            />

            <SecretField
              label="IPN Secret Key"
              value={ipnSecret}
              onChange={setIpnSecret}
              placeholder={
                config?.ipn_secret_set
                  ? "•••••••• (configurato)"
                  : "Inserisci l'IPN secret"
              }
              configured={config?.ipn_secret_set ?? false}
              disabled={saving}
            />
          </div>

          {/* Toggle */}
          <div className="flex flex-col gap-3 border-t border-line pt-4">
            <ToggleRow
              label="Sandbox mode"
              hint="Se attivo usa api-sandbox.nowpayments.io (ambiente di test)."
              checked={sandbox}
              onChange={setSandbox}
              disabled={saving}
            />
            <ToggleRow
              label="Enabled"
              hint="Se disattivo i depositi crypto sono disabilitati per gli utenti."
              checked={enabled}
              onChange={setEnabled}
              disabled={saving}
            />
          </div>

          {/* Footer: stato + azioni */}
          <div className="flex items-center justify-between gap-3 flex-wrap border-t border-line pt-4">
            <div className="text-[11.5px] text-muted">
              {updatedAtLabel
                ? `Ultimo aggiornamento: ${updatedAtLabel}`
                : "Mai configurato."}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleTest}
                disabled={testing || saving}
              >
                {testing ? "Test in corso…" : "Test connessione"}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ──────────────────────── sub-components ───────────────────────── */

/** Input password per una secret/key. Mostra il valore masked corrente come
 *  placeholder e un indicatore di stato (configurato / non configurato). */
function SecretField({
  label,
  value,
  onChange,
  placeholder,
  configured,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  configured: boolean;
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between gap-2">
        <span className="text-[11.5px] text-muted">{label}</span>
        {configured ? (
          <span className="text-[11px] text-yes">✓ Configurato</span>
        ) : (
          <span className="text-[11px] text-muted">Non configurato</span>
        )}
      </span>
      <input
        className="input font-mono"
        type="password"
        autoComplete="new-password"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </label>
  );
}

/** Riga con label + descrizione + toggle on/off in stile dark. */
function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="text-[13px] text-text font-medium">{label}</div>
        <div className="text-[11.5px] text-muted mt-0.5">{hint}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`relative h-6 w-11 shrink-0 rounded-pill border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          checked
            ? "bg-accent border-accent"
            : "bg-bg-3 border-line-strong"
        }`}
      >
        <span
          className={`absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white transition-transform ${
            checked ? "translate-x-[22px]" : "translate-x-[2px]"
          }`}
        />
      </button>
    </div>
  );
}

/** Box informativo: dove ottenere le credenziali + IPN callback URL. */
function InfoBox() {
  return (
    <section className="card bg-bg-2">
      <h2 className="text-[14px] font-semibold mb-2">Come configurare</h2>
      <p className="text-[12.5px] text-text-2 leading-relaxed">
        Le credenziali si ottengono dalla dashboard NOWPayments →{" "}
        <strong>Settings → API keys</strong> (API Key + Public Key) e{" "}
        <strong>Store settings → IPN</strong> (IPN Secret).
      </p>
      <p className="text-[12.5px] text-text-2 leading-relaxed mt-2">
        L&apos;IPN callback URL da configurare su NOWPayments è:
      </p>
      <code className="mt-1 block font-mono text-[12px] text-accent-2 break-all">
        {"<PUBLIC_BASE_URL>/v1/webhooks/nowpayments/ipn"}
      </code>
    </section>
  );
}
